import { useState, useMemo } from 'react';
import { WidgetCard } from './WidgetCard';
import { SeverityBadge } from './SeverityBadge';
import { MOCK_ALERTS, filterAlertsBySeverity, filterAlertsByDateRange } from '../mocks/alerts';
import type { Severity, DateRange } from '../types';

const SEVERITIES: (Severity | 'all')[] = ['all', 'critical', 'high', 'medium', 'low'];

function formatTimeAgo(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface AlertFeedWidgetProps {
  dateRange: DateRange;
}

export function AlertFeedWidget({ dateRange }: AlertFeedWidgetProps) {
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
  const filtered = useMemo(() => {
    const byDate = filterAlertsByDateRange(MOCK_ALERTS, dateRange.start, dateRange.end);
    return filterAlertsBySeverity(byDate, selectedSeverity);
  }, [selectedSeverity, dateRange]);

  return (
    <WidgetCard
      title="Alerts"
      action={
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
          {filtered.length}
        </span>
      }
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {SEVERITIES.map((severity) => (
          <SeverityBadge
            key={severity}
            severity={severity}
            interactive
            active={selectedSeverity === severity}
            onClick={() => setSelectedSeverity(severity)}
          />
        ))}
      </div>

      <div className="max-h-80 overflow-auto rounded-lg border border-gray-100">
        <ul className="divide-y divide-gray-100" role="list">
          {filtered.map((alert) => (
            <li
              key={alert.id}
              className="flex flex-col gap-2 px-3 py-3 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex items-center gap-3">
                <SeverityBadge severity={alert.severity} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{alert.ruleName}</p>
                  <p className="text-xs text-gray-500">{alert.customerName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 sm:text-sm">
                <span>{formatTimeAgo(alert.timestamp)}</span>
                <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                  {alert.status}
                </span>
              </div>
            </li>
          ))}
        </ul>
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">No alerts match this filter.</div>
        )}
      </div>
    </WidgetCard>
  );
}
